import React, { useState, useEffect } from 'react';
import { TextField, Text, Stack, MessageBar, MessageBarType, Button } from '@fluentui/react';
import { FiEdit2, FiTrash2, FiPlus, FiFolder, FiCheck, FiX } from 'react-icons/fi';
import { Dismiss24Regular, Save20Regular } from '@fluentui/react-icons';
import './TabManager.css';

const TabManager = ({ isOpen, onDismiss, tabs, onCreateTab, onEditTab, onDeleteTab, currentTabId, onSelectTab }) => {
    const [editingTabId, setEditingTabId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [error, setError] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const [panelWidth, setPanelWidth] = useState(600);
    const [isResizing, setIsResizing] = useState(false);

    const handleStartEdit = (tab) => {
        // Permettre l'édition de tous les tabs (y compris système)
        setEditingTabId(tab.id);
        setEditingName(tab.name);
        setError('');
    };

    const handleSaveEdit = () => {
        if (!editingName.trim()) {
            setError('Tab name cannot be empty');
            return;
        }

        const exists = tabs.some(t => 
            t.name.toLowerCase() === editingName.toLowerCase() && t.id !== editingTabId
        );

        if (exists) {
            setError('A tab with this name already exists');
            return;
        }

        onEditTab(editingTabId, editingName);
        setEditingTabId(null);
        setEditingName('');
        setError('');
    };

    const handleCancelEdit = () => {
        setEditingTabId(null);
        setEditingName('');
        setError('');
    };

    const handleDeleteTab = (tabId) => {
        const tab = tabs.find(t => t.id === tabId);
        if (!tab || tab.isSystemTab) {
            setError('Cannot delete system tabs');
            return;
        }

        // Confirmation avant suppression
        if (window.confirm(`Are you sure you want to delete the tab "${tab.name}"? All widgets in this tab will be removed.`)) {
            onDeleteTab(tabId);
        }
    };

    const handleStartCreate = () => {
        setIsCreating(true);
        setNewTabName('');
        setError('');
    };

    const handleSaveCreate = () => {
        if (!newTabName.trim()) {
            setError('Tab name cannot be empty');
            return;
        }

        const exists = tabs.some(t => 
            t.name.toLowerCase() === newTabName.toLowerCase()
        );

        if (exists) {
            setError('A tab with this name already exists');
            return;
        }

        onCreateTab(newTabName);
        setIsCreating(false);
        setNewTabName('');
        setError('');
    };

    const handleCancelCreate = () => {
        setIsCreating(false);
        setNewTabName('');
        setError('');
    };

    // Handle resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;
            const newWidth = window.innerWidth - e.clientX;
            if (newWidth >= 400 && newWidth <= 900) {
                setPanelWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing]);

    if (!isOpen) return null;

    return (
        <div
            className={`tab-manager-panel ${isOpen ? 'tab-manager-open' : ''}`}
            style={{ width: `${panelWidth}px` }}
        >
            {/* Resize handle */}
            <div
                className="tab-manager-resize-handle"
                onMouseDown={() => setIsResizing(true)}
            />

            {/* Header */}
            <div className="tab-manager-header">
                <h3 className="tab-manager-title">
                    <FiFolder size={20} />
                    Manage Tabs
                </h3>
                <div className="tab-manager-actions">
                    <Button
                        appearance="subtle"
                        icon={<Dismiss24Regular />}
                        onClick={onDismiss}
                        title="Close"
                    />
                </div>
            </div>

            <div className="tab-manager-subtitle">
                Create, edit, and organize your dashboard tabs
            </div>

            {/* Content */}
            <div className="tab-manager-content">
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => setError('')}
                        dismissButtonAriaLabel="Close"
                    >
                        {error}
                    </MessageBar>
                )}

                <div className="tabs-list">
                    {tabs.map((tab) => (
                        <div 
                            key={tab.id} 
                            className={`tab-item ${tab.id === currentTabId ? 'active' : ''} ${tab.isSystemTab ? 'system' : ''}`}
                            onClick={() => !editingTabId && onSelectTab && onSelectTab(tab.id)}
                        >
                            {editingTabId === tab.id ? (
                                <div className="tab-edit-mode">
                                    <TextField
                                        value={editingName}
                                        onChange={(e, newValue) => setEditingName(newValue || '')}
                                        placeholder="Tab name"
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleSaveEdit();
                                            if (e.key === 'Escape') handleCancelEdit();
                                        }}
                                    />
                                    <div className="tab-edit-actions">
                                        <button
                                            onClick={handleSaveEdit}
                                            className="save-btn"
                                            title="Save"
                                        >
                                            <FiCheck size={16} />
                                        </button>
                                        <button
                                            onClick={handleCancelEdit}
                                            className="cancel-btn"
                                            title="Cancel"
                                        >
                                            <FiX size={16} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="tab-info">
                                        <FiFolder className="tab-icon" />
                                        <div className="tab-details">
                                            <Text className="tab-name">
                                                {tab.name}
                                                {tab.isSystemTab && <span className="system-badge">System</span>}
                                            </Text>
                                        </div>
                                    </div>
                                    <div className="tab-actions">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleStartEdit(tab);
                                            }}
                                            title="Edit tab name"
                                            className="edit-btn"
                                        >
                                            <FiEdit2 size={16} />
                                        </button>
                                        {!tab.isSystemTab && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteTab(tab.id);
                                                }}
                                                title="Delete tab"
                                                className="delete-btn"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {isCreating ? (
                    <div className="new-tab-form">
                        <TextField
                            value={newTabName}
                            onChange={(e, newValue) => setNewTabName(newValue || '')}
                            placeholder="Enter tab name"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveCreate();
                                if (e.key === 'Escape') handleCancelCreate();
                            }}
                        />
                        <div className="new-tab-actions">
                            <Button appearance="primary" onClick={handleSaveCreate}>
                                <FiCheck size={16} style={{ marginRight: '6px' }} />
                                Create
                            </Button>
                            <Button appearance="secondary" onClick={handleCancelCreate}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <button className="add-tab-button" onClick={handleStartCreate}>
                        <FiPlus size={20} />
                        <span>Create New Tab</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default TabManager;
